<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['saved', 'deleted']);

const { t } = useI18n();
const { isAdmin } = useAdmin();
const companiesStore = useCompaniesStore();

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
const companyId = ref(null);
const isSaving = ref(false);
const form = reactive({ name: '', domain: '', description: '' });

const isEditing = computed(() => !!companyId.value);
const title = computed(() =>
  isEditing.value
    ? t('COMPANIES.FORM.EDIT_TITLE')
    : t('COMPANIES.FORM.NEW_TITLE')
);

const open = (company = null) => {
  companyId.value = company?.id || null;
  Object.assign(form, {
    name: company?.name || '',
    domain: company?.domain || '',
    description: company?.description || '',
  });
  dialogRef.value?.open();
};

const onSave = async () => {
  if (!form.name.trim()) {
    useAlert(t('COMPANIES.FORM.NAME_REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    const company = {
      name: form.name.trim(),
      domain: form.domain.trim() || null,
      description: form.description,
    };
    if (isEditing.value) {
      await companiesStore.update({ id: companyId.value, company });
    } else {
      await companiesStore.create({ company });
    }
    useAlert(t('COMPANIES.FORM.SAVE_SUCCESS'));
    dialogRef.value?.close();
    emit('saved');
  } catch (error) {
    useAlert(error.message || t('COMPANIES.FORM.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const onDelete = async () => {
  try {
    await companiesStore.delete(companyId.value);
    useAlert(t('COMPANIES.FORM.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
    dialogRef.value?.close();
    emit('deleted');
  } catch (error) {
    useAlert(error.message || t('COMPANIES.FORM.DELETE_ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="t('COMPANIES.FORM.SAVE')"
    :is-loading="isSaving"
    @confirm="onSave"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('COMPANIES.FORM.NAME')"
        :placeholder="t('COMPANIES.FORM.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="form.domain"
        :label="t('COMPANIES.FORM.DOMAIN')"
        :placeholder="t('COMPANIES.FORM.DOMAIN_PLACEHOLDER')"
      />
      <TextArea
        v-model="form.description"
        :label="t('COMPANIES.FORM.DESCRIPTION')"
        :placeholder="t('COMPANIES.FORM.DESCRIPTION_PLACEHOLDER')"
        :max-length="1000"
      />
      <Button
        v-if="isEditing && isAdmin"
        :label="t('COMPANIES.FORM.DELETE')"
        icon="i-lucide-trash-2"
        variant="ghost"
        color="ruby"
        size="sm"
        type="button"
        class="self-start"
        @click="deleteDialogRef?.open()"
      />
    </div>
  </Dialog>
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('COMPANIES.FORM.DELETE')"
    :description="t('COMPANIES.FORM.DELETE_CONFIRM', { name: form.name })"
    :confirm-button-label="t('COMPANIES.FORM.DELETE')"
    @confirm="onDelete"
  />
</template>
